/**
 * Servicio para manejo de variables de plantillas en el frontend
 */

import { fetchWithAuth } from './apiUtils';
import { getAuthToken } from './authService';
import { API_URL } from '../constants/api';

const API_BASE_URL = `${API_URL}/device-templates`;

/**
 * Obtiene todas las variables disponibles para plantillas
 * @returns {Promise<Object>} Variables organizadas por categoría y en lista
 */
export const getTemplateVariables = async () => {
  const token = getAuthToken();
  return await fetchWithAuth(`${API_BASE_URL}/variables`, {
    method: 'GET'
  }, token);
};

/**
 * Previsualiza una plantilla con variables procesadas
 * @param {string} deviceId - ID del dispositivo
 * @param {Object} data - Datos para la vista previa
 * @param {string} data.template - Plantilla a procesar
 * @param {string} data.contactId - ID del contacto (opcional)
 * @param {string} data.eventName - Nombre del evento (opcional)
 * @returns {Promise<Object>} Plantilla procesada
 */
export const previewTemplate = async (deviceId, data) => {
  const token = getAuthToken();
  return await fetchWithAuth(`${API_BASE_URL}/${deviceId}/preview`, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json'
    }
  }, token);
};

/**
 * Previsualiza una plantilla con datos reales de dispositivo y contacto
 * @param {string} deviceId - ID del dispositivo
 * @param {Object} data - Datos para la vista previa
 * @param {string} data.template - Plantilla a procesar
 * @param {string} data.contactId - ID del contacto (opcional)
 * @returns {Promise<Object>} Plantilla procesada con datos reales
 */
export const previewTemplateAdvanced = async (deviceId, data) => {
  const token = getAuthToken();
  return await fetchWithAuth(`${API_BASE_URL}/${deviceId}/preview-advanced`, {
    method: 'POST',
    body: JSON.stringify(data),
    headers: {
      'Content-Type': 'application/json'
    }
  }, token);
};

/**
 * Formatea variables para mostrar en interfaz
 * @param {Object} variables - Variables obtenidas del backend
 * @returns {Array} Array de variables formateadas para UI
 */
export const formatVariablesForUI = (variables) => {
  if (!variables || !variables.list) {
    return [];
  }

  return variables.list.map(variable => ({
    ...variable,
    displayName: variable.variable.replace(/[{}]/g, ''), // Remover llaves para display
    categoryName: getCategoryDisplayName(variable.category)
  }));
};

/**
 * Agrupa variables por categoría para mostrar
 * @param {Object} variables - Variables obtenidas del backend
 * @returns {Object} Variables agrupadas por categoría
 */
export const groupVariablesByCategory = (variables) => {
  if (!variables || !variables.by_category) {
    return {};
  }

  const grouped = {};
  
  Object.entries(variables.by_category).forEach(([category, categoryVars]) => {
    grouped[category] = {
      displayName: getCategoryDisplayName(category),
      variables: Object.entries(categoryVars).map(([variable, description]) => ({
        variable,
        description,
        displayName: variable.replace(/[{}]/g, '')
      }))
    };
  });

  return grouped;
};

/**
 * Obtiene el nombre de display para una categoría
 * @param {string} category - Categoría interna
 * @returns {string} Nombre para mostrar
 */
const getCategoryDisplayName = (category) => {
  const categoryNames = {
    device: '📱 Dispositivo',
    contact: '👤 Contacto',
    subscription: '💳 Suscripción',
    datetime: '📅 Fecha y Hora',
    system: '⚙️ Sistema'
  };

  return categoryNames[category] || category;
};

/**
 * Inserta una variable en el texto del textarea
 * @param {string} variable - Variable a insertar (ej: "{{nombre_contacto}}")
 * @param {HTMLTextAreaElement} textareaElement - Elemento textarea
 */
export const insertVariableInTextarea = (variable, textareaElement) => {
  if (!textareaElement) return;

  const startPos = textareaElement.selectionStart;
  const endPos = textareaElement.selectionEnd;
  const text = textareaElement.value;
  
  const newText = text.substring(0, startPos) + variable + text.substring(endPos);
  
  // Actualizar el valor
  textareaElement.value = newText;
  
  // Mover el cursor después de la variable insertada
  const newPos = startPos + variable.length;
  textareaElement.setSelectionRange(newPos, newPos);
  
  // Disparar evento de cambio
  const changeEvent = new Event('input', { bubbles: true });
  textareaElement.dispatchEvent(changeEvent);
  
  // Enfocar el textarea
  textareaElement.focus();
};

export default {
  getTemplateVariables,
  previewTemplate,
  previewTemplateAdvanced,
  formatVariablesForUI,
  groupVariablesByCategory,
  insertVariableInTextarea
}; 